import React, { createContext, useContext, useEffect, useState } from "react";
import { storage } from "../Utils/storageService";

export const THEMES = {
  THEME1: "theme1",
  THEME2: "theme2",
};

const ThemeContext = createContext({
  theme: THEMES.THEME1,
  setTheme: () => {},
  toggleTheme: () => {},
  isTheme1: true,
  isTheme2: false,
});

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const savedTheme = storage.getTheme();
    return savedTheme === THEMES.THEME2 ? THEMES.THEME2 : THEMES.THEME1;
  });

  const applyThemeToDOM = (activeTheme) => {
    // Set data-theme on html and body for attribute-based CSS selectors
    document.documentElement.setAttribute("data-theme", activeTheme);
    document.body.setAttribute("data-theme", activeTheme);

    // Sync theme classes on body for class-based CSS selectors
    document.body.classList.remove(THEMES.THEME1, THEMES.THEME2);
    document.body.classList.add(activeTheme);
  };

  useEffect(() => {
    applyThemeToDOM(theme);

    // Listen for storage changes across tabs or windows
    const handleStorageChange = (e) => {
      if (e.key === "app_theme" && (e.newValue === THEMES.THEME1 || e.newValue === THEMES.THEME2)) {
        setThemeState(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [theme]);

  const setTheme = (newTheme) => {
    const validTheme = newTheme === THEMES.THEME2 ? THEMES.THEME2 : THEMES.THEME1;
    storage.setTheme(validTheme);
    setThemeState(validTheme);
  };

  const toggleTheme = () => {
    setTheme(theme === THEMES.THEME1 ? THEMES.THEME2 : THEMES.THEME1);
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isTheme1: theme === THEMES.THEME1,
    isTheme2: theme === THEMES.THEME2,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export default ThemeContext;
